import apiClient from "./api";

/** How the webhook responds: async returns 202 immediately, synchronous waits and returns 200 with output. */
export type AutonomousExecutionMode = "Async" | "Synchronous";

/**
 * Lifecycle status of a single execution (one webhook kick).
 *
 * `WaitingForApproval` is not terminal: the run parked one or more tool calls for a human and
 * resumes once they are approved, so keep polling the run endpoint.
 */
export type AutonomousExecutionStatus =
  | "Pending"
  | "Running"
  | "Completed"
  | "Failed"
  | "WaitingForApproval";

export interface AutonomousAgent {
  id: string;
  name: string;
  prompt: string;
  widgetId: string;
  isActive: boolean;
  executionMode: AutonomousExecutionMode;
  lastTriggeredAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Total recorded executions for this agent. */
  executionCount: number;
  /** Relative webhook path, e.g. /api/autonomous/hook/{companyId}/{agentId}. Prepend the API origin for the absolute URL. */
  webhookPath: string;
}

export interface CreateAutonomousAgentRequest {
  name: string;
  prompt: string;
  widgetId: string;
  executionMode?: AutonomousExecutionMode;
}

export interface UpdateAutonomousAgentRequest {
  name?: string;
  prompt?: string;
  widgetId?: string;
  isActive?: boolean;
  executionMode?: AutonomousExecutionMode;
}

export interface AutonomousAgentExecutionLog {
  id: string;
  autonomousAgentId: string;
  chatSessionId: string | null;
  status: AutonomousExecutionStatus;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  triggerSource: string | null;
  /** External id of the end-user this run was attributed to, when triggered by a per-user token. */
  customerExternalId: string | null;
  createdAt: string;
}

/** Point-in-time status of one run, plus its output (Completed) or error (Failed). */
export interface AutonomousRunStatus {
  executionId: string;
  status: AutonomousExecutionStatus;
  startedAt: string;
  completedAt: string | null;
  /** Wall-clock run time in milliseconds; present once the run has finished. */
  durationMs: number | null;
  /** The agent's generated answer. Present only when status is "Completed". */
  output: string | null;
  /** Failure reason. Present only when status is "Failed". */
  error: string | null;
  /** Tool calls parked for a human. Non-empty only when status is "WaitingForApproval". */
  pendingApprovalIds: string[];
}

export const autonomousAgentService = {
  getAll: (): Promise<AutonomousAgent[]> =>
    apiClient.get("/autonomous-agents").then((r) => r.data),

  getById: (id: string): Promise<AutonomousAgent> =>
    apiClient.get(`/autonomous-agents/${id}`).then((r) => r.data),

  create: (data: CreateAutonomousAgentRequest): Promise<AutonomousAgent> =>
    apiClient.post("/autonomous-agents", data).then((r) => r.data),

  update: (
    id: string,
    data: UpdateAutonomousAgentRequest,
  ): Promise<AutonomousAgent> =>
    apiClient.put(`/autonomous-agents/${id}`, data).then((r) => r.data),

  remove: (id: string): Promise<void> =>
    apiClient.delete(`/autonomous-agents/${id}`).then((r) => r.data),

  getLogs: (id: string, limit = 20): Promise<AutonomousAgentExecutionLog[]> =>
    apiClient
      .get(`/autonomous-agents/${id}/logs?limit=${limit}`)
      .then((r) => r.data),

  getRunOutput: (
    agentId: string,
    executionId: string,
  ): Promise<AutonomousRunStatus> =>
    apiClient
      .get(`/autonomous-agents/${agentId}/runs/${executionId}`)
      .then((r) => r.data),
};
